import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Navigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Link,
  Paper,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CloseIcon from '@mui/icons-material/Close';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditIcon from '@mui/icons-material/Edit';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import RefreshIcon from '@mui/icons-material/Refresh';
import StorefrontIcon from '@mui/icons-material/Storefront';
import SyncAltIcon from '@mui/icons-material/SyncAlt';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { toast } from 'react-toastify';

import AuthContext from '../../context/authContext';
import useGoogleBusiness from '../../hooks/useGoogleBusiness';
import GoogleRatingMatchDialog from '../Entertainment/GoogleRatingMatchDialog';
import { viewRootTextPrimarySx } from '../../util/chartTheme';

// Highlight colour for a day whose Google hours differ from our internal hours.
const DISCREPANCY_BG = 'rgba(211, 47, 47, 0.10)';

const fmtIntervals = intervals => {
  if (!Array.isArray(intervals) || intervals.length === 0) return 'Geschlossen';
  return intervals.map(iv => `${iv.start}–${iv.end}`).join(', ');
};

// Builds an editable week map ({ '0': [{start,end}], … }) from the per-day
// comparison rows, reading either the Google side or the internal side.
const daysToWeek = (days, side) => {
  const week = { 0: [], 1: [], 2: [], 3: [], 4: [], 5: [], 6: [] };
  (days || []).forEach(d => {
    week[d.weekday] = (d[side] || []).map(iv => ({ start: iv.start, end: iv.end }));
  });
  return week;
};

// Editor dialog: lets the user adjust the Google opening hours of one
// entertainment (per-day intervals) and either save them or prefill from our
// internal schedule.
function GoogleHoursEditDialog({ open, item, onClose, onSave, saving }) {
  // week: { weekday -> [{ start, end }] }
  const [week, setWeek] = useState(() => daysToWeek(item?.days, 'google'));

  useEffect(() => {
    if (open) setWeek(daysToWeek(item?.days, 'google'));
  }, [open, item]);

  const orderedDays = useMemo(() => (item?.days || []).map(d => ({ weekday: d.weekday, label: d.label })), [item]);

  const setDay = (weekday, intervals) => setWeek(prev => ({ ...prev, [weekday]: intervals }));

  const addInterval = weekday =>
    setDay(weekday, [...(week[weekday] || []), { start: '10:00', end: '22:00' }]);

  const removeInterval = (weekday, idx) =>
    setDay(weekday, (week[weekday] || []).filter((_, i) => i !== idx));

  const updateInterval = (weekday, idx, field, value) =>
    setDay(
      weekday,
      (week[weekday] || []).map((iv, i) => (i === idx ? { ...iv, [field]: value } : iv))
    );

  const toggleClosed = (weekday, closed) =>
    setDay(weekday, closed ? [] : [{ start: '10:00', end: '22:00' }]);

  const loadInternal = () => setWeek(daysToWeek(item?.days, 'internal'));

  const handleSave = () => onSave(week);

  if (!item) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth='sm' fullWidth>
      <DialogTitle sx={{ pr: 6 }}>
        Google-Öffnungszeiten bearbeiten
        <Typography variant='body2' sx={{ color: 'text.secondary' }}>
          {item.longName}
          {item.locationTitle ? ` · ${item.locationTitle}` : ''}
        </Typography>
        <IconButton onClick={onClose} sx={{ position: 'absolute', right: 8, top: 8 }} size='small'>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5, flexWrap: 'wrap', gap: 1 }}>
          <Typography variant='caption' sx={{ color: 'text.secondary' }}>
            Diese Zeiten werden direkt im Google-Business-Profil gespeichert.
          </Typography>
          {item.hasInternal && (
            <Button size='small' startIcon={<SyncAltIcon />} onClick={loadInternal}>
              Interne Zeiten laden
            </Button>
          )}
        </Box>

        {orderedDays.map(({ weekday, label }) => {
          const intervals = week[weekday] || [];
          const closed = intervals.length === 0;
          return (
            <Box key={weekday} sx={{ mb: 1.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant='body2' sx={{ fontWeight: 600, width: 110 }}>
                  {label}
                </Typography>
                <Switch
                  size='small'
                  checked={!closed}
                  onChange={e => toggleClosed(weekday, !e.target.checked)}
                />
                <Typography variant='caption' sx={{ color: 'text.secondary' }}>
                  {closed ? 'Geschlossen' : 'Geöffnet'}
                </Typography>
                {!closed && (
                  <Tooltip title='Intervall hinzufügen'>
                    <IconButton size='small' onClick={() => addInterval(weekday)}>
                      <AddIcon fontSize='small' />
                    </IconButton>
                  </Tooltip>
                )}
              </Box>
              {intervals.map((iv, idx) => (
                <Box key={idx} sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.75, ml: '110px' }}>
                  <TextField
                    type='time'
                    size='small'
                    value={iv.start}
                    onChange={e => updateInterval(weekday, idx, 'start', e.target.value)}
                    inputProps={{ step: 300 }}
                  />
                  <Typography variant='body2'>–</Typography>
                  <TextField
                    type='time'
                    size='small'
                    value={iv.end}
                    onChange={e => updateInterval(weekday, idx, 'end', e.target.value)}
                    inputProps={{ step: 300 }}
                  />
                  <IconButton size='small' onClick={() => removeInterval(weekday, idx)}>
                    <DeleteOutlineIcon fontSize='small' />
                  </IconButton>
                </Box>
              ))}
            </Box>
          );
        })}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} color='inherit'>
          Abbrechen
        </Button>
        <Button
          variant='contained'
          onClick={handleSave}
          disabled={saving}
          startIcon={saving ? <CircularProgress size={18} color='inherit' /> : null}
        >
          Auf Google speichern
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// One entertainment's comparison card: weekly table with discrepancies in red
// plus the actions to push internal hours or edit the Google hours.
function EntertainmentHoursCard({ item, canUpdate, onPushInternal, onEdit, busy }) {
  return (
    <Paper variant='outlined' sx={{ p: 2, mb: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, flexWrap: 'wrap', mb: 1 }}>
        <Box sx={{ flex: 1, minWidth: 200 }}>
          <Typography variant='subtitle1' sx={{ fontWeight: 700 }}>
            {item.longName}
          </Typography>
          <Typography variant='caption' sx={{ color: 'text.secondary' }}>
            {[item.branchName, item.locationTitle].filter(Boolean).join(' · ') || '—'}
            {item.googleMapsUri && (
              <Link href={item.googleMapsUri} target='_blank' rel='noopener' sx={{ ml: 0.5, display: 'inline-flex', verticalAlign: 'middle' }}>
                <OpenInNewIcon sx={{ fontSize: 14 }} />
              </Link>
            )}
          </Typography>
        </Box>
        {item.hasDiscrepancy ? (
          <Chip color='error' size='small' icon={<WarningAmberIcon />} label='Abweichungen' />
        ) : item.hasInternal ? (
          <Chip color='success' size='small' icon={<CheckCircleIcon />} label='Synchron' />
        ) : (
          <Chip color='default' size='small' variant='outlined' label='Kein interner Plan' />
        )}
      </Box>

      {item.googleError && (
        <Alert severity='error' sx={{ mb: 1 }}>
          Google-Öffnungszeiten konnten nicht geladen werden: {item.googleError}
        </Alert>
      )}
      {!item.hasInternal && !item.googleError && (
        <Alert severity='info' sx={{ mb: 1 }}>
          Für dieses Entertainment sind keine internen Öffnungszeiten hinterlegt – es gibt nichts zu
          vergleichen. Die bei Google hinterlegten Zeiten sind unten dargestellt.
        </Alert>
      )}

      <Table size='small'>
        <TableHead>
          <TableRow>
            <TableCell sx={{ fontWeight: 600, width: 120 }}>Tag</TableCell>
            <TableCell sx={{ fontWeight: 600 }}>Unsere Zeiten</TableCell>
            <TableCell sx={{ fontWeight: 600 }}>Google-Zeiten</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {(item.days || []).map(d => (
            <TableRow key={d.weekday} sx={{ bgcolor: d.discrepant ? DISCREPANCY_BG : 'transparent' }}>
              <TableCell sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                {d.discrepant && (
                  <Tooltip title='Google weicht von unseren Öffnungszeiten ab'>
                    <WarningAmberIcon sx={{ fontSize: 16, color: 'error.main' }} />
                  </Tooltip>
                )}
                {d.label}
              </TableCell>
              <TableCell sx={{ color: item.hasInternal ? 'text.primary' : 'text.disabled' }}>
                {item.hasInternal ? fmtIntervals(d.internal) : '—'}
              </TableCell>
              <TableCell
                sx={{
                  color: d.discrepant ? 'error.main' : 'text.primary',
                  fontWeight: d.discrepant ? 600 : 400,
                }}
              >
                {fmtIntervals(d.google)}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      <Box sx={{ display: 'flex', gap: 1.5, mt: 1.5, flexWrap: 'wrap' }}>
        <Tooltip title={item.hasInternal ? '' : 'Es sind keine internen Öffnungszeiten hinterlegt.'}>
          <span>
            <Button
              variant='contained'
              size='small'
              startIcon={busy ? <CircularProgress size={16} color='inherit' /> : <SyncAltIcon />}
              onClick={() => onPushInternal(item)}
              disabled={!canUpdate || busy || !item.hasInternal || !item.hasDiscrepancy}
            >
              Interne Zeiten auf Google übertragen
            </Button>
          </span>
        </Tooltip>
        <Button
          variant='outlined'
          size='small'
          startIcon={<EditIcon />}
          onClick={() => onEdit(item)}
          disabled={!canUpdate || busy || !!item.googleError}
        >
          Google-Zeiten bearbeiten
        </Button>
      </Box>
    </Paper>
  );
}

function GoogleOpeningHours() {
  const authCtx = useContext(AuthContext);
  const canUpdate = authCtx.checkRight('googleBusiness:update');

  const { getOpeningHoursOverview, updateOpeningHours } = useGoogleBusiness();
  const apiRef = useRef();
  apiRef.current = { getOpeningHoursOverview, updateOpeningHours };

  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [editItem, setEditItem] = useState(null);
  const [savingEdit, setSavingEdit] = useState(false);
  const [matchOpen, setMatchOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiRef.current.getOpeningHoursOverview();
      setData(res);
    } catch (_) {
      // toast handled by hook
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const items = useMemo(() => data?.items || [], [data]);
  const googleConfigured = data ? data.googleConfigured : true;

  const summary = useMemo(() => {
    const withDiff = items.filter(it => it.hasDiscrepancy).length;
    const synced = items.filter(it => it.hasInternal && !it.hasDiscrepancy).length;
    return { withDiff, synced, total: items.length };
  }, [items]);

  // Replaces one item's comparison rows after a successful write to Google.
  const applyUpdatedItem = (entertainmentId, updated) =>
    setData(prev => {
      if (!prev) return prev;
      const next = {
        ...prev,
        items: prev.items.map(it =>
          it.entertainmentId === entertainmentId
            ? { ...it, days: updated.days, hasDiscrepancy: updated.hasDiscrepancy, hasInternal: updated.hasInternal, googleError: null }
            : it
        ),
      };
      return next;
    });

  const handlePushInternal = async item => {
    setBusyId(item.entertainmentId);
    try {
      const res = await apiRef.current.updateOpeningHours(item.entertainmentId, { source: 'internal' });
      applyUpdatedItem(item.entertainmentId, res);
      toast.success('Interne Öffnungszeiten an Google übertragen');
    } catch (_) {
      // toast handled by hook
    } finally {
      setBusyId(null);
    }
  };

  const handleSaveEdit = async week => {
    if (!editItem) return;
    setSavingEdit(true);
    try {
      const res = await apiRef.current.updateOpeningHours(editItem.entertainmentId, { week });
      applyUpdatedItem(editItem.entertainmentId, res);
      toast.success('Google-Öffnungszeiten gespeichert');
      setEditItem(null);
    } catch (_) {
      // toast handled by hook
    } finally {
      setSavingEdit(false);
    }
  };

  if (!authCtx.checkRight('googleBusiness:read')) {
    return <Navigate to='/welcome' replace />;
  }

  return (
    <Box sx={{ ...viewRootTextPrimarySx, p: { xs: 1.5, md: 3 } }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1, mb: 1 }}>
        <Typography variant='h5' sx={{ fontWeight: 700 }}>
          Google-Öffnungszeiten
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Button
            variant='outlined'
            size='small'
            startIcon={<StorefrontIcon />}
            onClick={() => setMatchOpen(true)}
          >
            Standorte zuordnen
          </Button>
          <Button
            variant='outlined'
            size='small'
            startIcon={loading ? <CircularProgress size={16} /> : <RefreshIcon />}
            onClick={load}
            disabled={loading}
          >
            Aktualisieren
          </Button>
        </Box>
      </Box>
      <Typography variant='body2' sx={{ color: 'text.secondary', mb: 2, maxWidth: 900 }}>
        Vergleich unserer hinterlegten Öffnungszeiten mit den im Google-Business-Profil gespeicherten
        Zeiten je zugeordnetem Entertainment. Abweichende Tage sind <strong>rot</strong> markiert. Du
        kannst die internen Zeiten mit einem Klick an Google übertragen oder die Google-Zeiten direkt
        bearbeiten.
      </Typography>

      {!loading && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 2 }}>
          <Chip
            size='small'
            color={summary.withDiff > 0 ? 'error' : 'default'}
            variant={summary.withDiff > 0 ? 'filled' : 'outlined'}
            icon={summary.withDiff > 0 ? <WarningAmberIcon /> : undefined}
            label={`${summary.withDiff} mit Abweichungen`}
          />
          <Chip size='small' color='success' variant='outlined' label={`${summary.synced} synchron`} />
          <Chip size='small' variant='outlined' label={`${summary.total} zugeordnet`} />
        </Box>
      )}

      <Divider sx={{ mb: 2 }} />

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : !googleConfigured ? (
        <Alert severity='warning'>
          Das Google-Business-Profil ist nicht konfiguriert. Öffnungszeiten können nicht abgeglichen
          werden.
        </Alert>
      ) : items.length === 0 ? (
        <Alert
          severity='info'
          action={
            <Button color='inherit' size='small' startIcon={<StorefrontIcon />} onClick={() => setMatchOpen(true)}>
              Standorte zuordnen
            </Button>
          }
        >
          Es sind keine Entertainments einem Google-Standort zugeordnet. Lege die Zuordnung zuerst
          über „Standorte zuordnen“ an.
        </Alert>
      ) : (
        items.map(item => (
          <EntertainmentHoursCard
            key={item.entertainmentId}
            item={item}
            canUpdate={canUpdate}
            busy={busyId === item.entertainmentId}
            onPushInternal={handlePushInternal}
            onEdit={setEditItem}
          />
        ))
      )}

      <GoogleHoursEditDialog
        open={!!editItem}
        item={editItem}
        onClose={() => setEditItem(null)}
        onSave={handleSaveEdit}
        saving={savingEdit}
      />

      <GoogleRatingMatchDialog
        open={matchOpen}
        onClose={() => setMatchOpen(false)}
        onConfirmed={load}
      />
    </Box>
  );
}

export default GoogleOpeningHours;
